import { z } from "zod";
import { RESOURCE_KINDS } from "./economy.js";

/**
 * The shape a village's economy travels in (05 Data Model, `village` entity).
 *
 * One request answers the whole screen: what the village holds now, what it
 * makes, what it can hold, and for every building what the next level costs and
 * whether it can be paid for. The alternative — a resources call plus a call
 * per building — is seventeen round trips on a phone to draw one list.
 *
 * Affordability is computed HERE, on the server, and sent as an answer rather
 * than as inputs the client works out for itself. The client may still predict
 * between polls (it has the same functions), but the server's answer is the one
 * that decides, and a client that computed a different one would grey out a
 * button the server would have allowed.
 */

export const WireResources = z.object({
  wood: z.number().int().nonnegative(),
  clay: z.number().int().nonnegative(),
  iron: z.number().int().nonnegative(),
  grain: z.number().int().nonnegative(),
});
export type WireResources = z.infer<typeof WireResources>;

/** Production can be negative on grain when an army eats more than the farms make. */
export const WireProduction = z.object({
  wood: z.number().int(),
  clay: z.number().int(),
  iron: z.number().int(),
  grain: z.number().int(),
});

export const WirePrerequisite = z.object({
  building: z.string(),
  level: z.number().int().positive(),
  /** What the village actually has, so the UI can say "Barracks 3 of 10". */
  have: z.number().int().nonnegative(),
});

export const WireNextLevel = z.object({
  level: z.number().int().positive(),
  cost: WireResources,
  total: z.number().int().nonnegative(),
  timeSeconds: z.number().int().nonnegative(),
  /**
   * How long it will actually take here: after the Main Building and the
   * beginner boost (29 Sep 2026). `timeSeconds` is the sheet's base figure.
   */
  durationMs: z.number().int().nonnegative().optional(),
  affordable: z.boolean(),
  /** What is still missing, per resource. Absent keys are covered. */
  shortfall: WireResources.partial(),
  /**
   * Milliseconds until the village could pay for it, or null for never —
   * nothing produces what it needs, or its store could never hold the price.
   */
  readyInMs: z.number().int().nonnegative().nullable(),
});

export const WireBuilding = z.object({
  id: z.string(),
  name: z.string(),
  /** 0 means "not built yet", which is a row the screen still has to show. */
  level: z.number().int().nonnegative(),
  maxLevel: z.number().int().positive(),
  produces: z.enum(RESOURCE_KINDS).nullable(),
  /** Null at max level: there is nothing to buy. */
  next: WireNextLevel.nullable(),
  /** Empty when nothing stands in the way. */
  missingPrerequisites: z.array(WirePrerequisite),
});

/** One thing being built. `id` is what a cancel refers to. */
export const WireQueueItem = z.object({
  id: z.string().uuid(),
  building: z.string(),
  name: z.string(),
  level: z.number().int().positive(),
  startedAtMs: z.number().int().nonnegative(),
  finishAtMs: z.number().int().nonnegative(),
});
export type WireQueueItem = z.infer<typeof WireQueueItem>;

/** One training order. Units arrive one at a time until it is empty. */
export const WireTraining = z.object({
  id: z.string().uuid(),
  unit: z.string(),
  name: z.string(),
  count: z.number().int().positive(),
  delivered: z.number().int().nonnegative(),
  startedAtMs: z.number().int().nonnegative(),
  perUnitMs: z.number().int().positive(),
  /** When the next one arrives, and when the whole order is done. */
  nextAtMs: z.number().int().nonnegative(),
  finishAtMs: z.number().int().nonnegative(),
});
export type WireTraining = z.infer<typeof WireTraining>;

export const WireUnit = z.object({
  trainingBlock: z.string().nullable().optional(),
  id: z.string(),
  name: z.string(),
  role: z.string(),
  have: z.number().int().nonnegative(),
  upkeepGrainPerHour: z.number().int().nonnegative(),
  cost: WireResources,
  trainSeconds: z.number().int().nonnegative(),
  /** Null when this village can already train it. */
  needs: z
    .object({ building: z.string(), name: z.string(), level: z.number().int().positive() })
    .nullable(),
  /** How many the village could pay for right now. */
  affordable: z.number().int().nonnegative(),
});
export type WireUnit = z.infer<typeof WireUnit>;

export const VillageEconomy = z.object({
  deploymentSupply: z.number().int().positive().optional(),
  pendingWagons: z.number().int().nonnegative().optional(),
  villageId: z.string().uuid(),
  /** The server's clock, so a client with a wrong one still ticks correctly. */
  nowMs: z.number().int().nonnegative(),
  resources: WireResources,
  resourceRemainder: WireProduction.optional(),
  capacity: WireResources,
  productionPerHour: WireProduction,
  /** Grain eaten per hour by the army. Zero until training lands. */
  grainUpkeepPerHour: z.number().int().nonnegative(),
  buildings: z.array(WireBuilding),
  /** What is being built, oldest first. */
  queue: z.array(WireQueueItem),
  /** How many things this village can build at once (1, or 2 at Main Building 10). */
  buildSlots: z.number().int().positive(),
  /** Every unit: what the village has, what it costs, what it needs. */
  units: z.array(WireUnit),
  /** What is being trained, per building line. */
  training: z.array(WireTraining),
  /**
   * First-day boost (29 Sep 2026): builds 3× faster and two at once until
   * this moment. Null once it is over.
   */
  beginnerBoostUntilMs: z.number().int().nonnegative().nullable().optional(),
  /** When the next supply wagon can be claimed (29 Sep 2026). */
  chestReadyAtMs: z.number().int().nonnegative().nullable().optional(),
});
export type VillageEconomy = z.infer<typeof VillageEconomy>;
export type WireBuilding = z.infer<typeof WireBuilding>;
export type WireNextLevel = z.infer<typeof WireNextLevel>;

/** POST /villages/{id}/build — start the next level of one building. */
export const BuildRequest = z.object({ building: z.string() });
export type BuildRequest = z.infer<typeof BuildRequest>;

/**
 * The answer to starting or cancelling a build: the whole village again.
 *
 * Not "ok: true". The client's next frame has to show the new queue, the
 * resources that were just spent and every button that has become affordable
 * or unaffordable as a result — and a second round trip to find that out is a
 * second chance for the two views to disagree.
 */
export const BuildResponse = z.object({
  started: WireQueueItem.nullable(),
  village: VillageEconomy,
});
export type BuildResponse = z.infer<typeof BuildResponse>;

/** POST /villages/{id}/train — order units. */
export const TrainRequest = z.object({
  unit: z.string(),
  count: z.number().int().positive(),
});
export type TrainRequest = z.infer<typeof TrainRequest>;

export const TrainResponse = z.object({
  ordered: WireTraining.nullable(),
  village: VillageEconomy,
});
export type TrainResponse = z.infer<typeof TrainResponse>;
